// ============================================================================
// ADD-ON HOOKS
// ============================================================================
// React hooks for the Add-on master module — clones the useMealPlan shape.

import { useState, useEffect, useCallback } from 'react';
import { addonService } from 'api/services/addonService';
import type { ID } from 'api/types';
import type {
  Addon,
  CreateAddonRequest,
  UpdateAddonRequest,
  AddonSearchRequest,
} from 'api/types/addon.types';

export const useAddons = (params?: AddonSearchRequest) => {
  const [data, setData] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchAddons = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await addonService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchAddons();
  }, [fetchAddons]);

  return { data, loading, error, pagination, refetch: fetchAddons };
};

export const useAddon = (id: ID) => {
  const [data, setData] = useState<Addon | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAddon = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await addonService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAddon();
  }, [fetchAddon]);

  return { data, loading, error, refetch: fetchAddon };
};

export const useCreateAddon = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createAddon = async (
    data: CreateAddonRequest,
    options?: { onSuccess?: (addon: Addon) => void; onError?: (error: Error) => void; }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await addonService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { createAddon, loading, error, success };
};

export const useUpdateAddon = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateAddon = async (
    id: ID,
    data: UpdateAddonRequest,
    options?: { onSuccess?: (addon: Addon) => void; onError?: (error: Error) => void; }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await addonService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { updateAddon, loading, error, success };
};

export const useDeleteAddon = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteAddon = async (
    id: ID,
    rowVersion: number,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void; }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await addonService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { deleteAddon, loading, error, success };
};
